// =====================================================================
// MPS-263 · KYC-101 — Identifier validation
// Author: Akarsh Singh | 2026-08-29
//
// Applies a country's rules to what the supplier typed, and returns EVERY
// problem rather than the first one, so the screen can mark all the offending
// fields at once instead of making the supplier fix them one at a time.
// =====================================================================
import {
  KycCountryRules,
  KycIdentifierField,
  getKycCountryRules,
} from './kyc-country-config';

export interface KycIdentifierProblem {
  /** Which field failed, so the screen can attach the message to it. */
  fieldKey: string;
  message: string;
}

/** The identifiers as the wizard holds them: field key to entered value. */
export type KycIdentifierValues = Record<string, string | null | undefined>;

function valueOf(values: KycIdentifierValues, key: string): string {
  return (values?.[key] ?? '').toString().trim();
}

/** A required field with nothing in it. */
function checkRequired(
  field: KycIdentifierField,
  values: KycIdentifierValues
): KycIdentifierProblem | null {
  if (!field.required) { return null; }
  if (valueOf(values, field.key)) { return null; }
  return { fieldKey: field.key, message: `${field.label} is required` };
}

/**
 * The format pattern. Skipped when the field is empty, because that is already
 * reported as a required-field problem and saying both would be noise.
 */
function checkPattern(
  field: KycIdentifierField,
  values: KycIdentifierValues
): KycIdentifierProblem | null {
  const value = valueOf(values, field.key);
  if (!value || !field.regex) { return null; }

  let matches: boolean;
  try {
    matches = new RegExp(field.regex).test(value);
  } catch {
    // A malformed pattern in the config must not block a supplier who has
    // done nothing wrong. Let the value through; the admin verifies it anyway.
    return null;
  }

  if (matches) { return null; }
  return {
    fieldKey: field.key,
    message: field.error || `${field.label} is not in the expected format`,
  };
}

/**
 * Cross-field checks, e.g. an Indian GSTIN carries the PAN in characters 3 to
 * 12. Skipped unless both fields are filled, so a half-completed form does not
 * accuse the supplier of a mismatch they have not made yet.
 */
function checkCrossFieldRules(
  rules: KycCountryRules,
  values: KycIdentifierValues
): KycIdentifierProblem[] {
  const problems: KycIdentifierProblem[] = [];

  for (const rule of rules.rules || []) {
    const source = valueOf(values, rule.source);
    const target = valueOf(values, rule.target);
    if (!source || !target) { continue; }

    // start is 1-based in the config because that is how the rule reads in
    // plain words: "characters 3 to 12".
    const slice = source.substring(rule.start - 1, rule.start - 1 + rule.length);
    if (slice.toUpperCase() !== target.toUpperCase()) {
      problems.push({ fieldKey: rule.source, message: rule.error });
    }
  }

  return problems;
}

/**
 * Every problem with the identifiers for one country.
 * An empty array means the set is good to save.
 */
export function validateKycIdentifiers(
  countryCode: string | null | undefined,
  values: KycIdentifierValues
): KycIdentifierProblem[] {
  const rules = getKycCountryRules(countryCode);
  const problems: KycIdentifierProblem[] = [];

  for (const field of rules.fields) {
    const missing = checkRequired(field, values);
    if (missing) { problems.push(missing); continue; }

    const malformed = checkPattern(field, values);
    if (malformed) { problems.push(malformed); }
  }

  problems.push(...checkCrossFieldRules(rules, values));
  return problems;
}

/** Validation for a single field, for checking on blur. */
export function validateKycIdentifierField(
  countryCode: string | null | undefined,
  fieldKey: string,
  values: KycIdentifierValues
): KycIdentifierProblem | null {
  return validateKycIdentifiers(countryCode, values)
    .find((p) => p.fieldKey === fieldKey) || null;
}

/** Shapes the values for the API: only fields the chosen country asks for. */
export function toIdentifierPayload(
  countryCode: string | null | undefined,
  values: KycIdentifierValues
): { fieldKey: string; fieldValue: string }[] {
  return getKycCountryRules(countryCode).fields.map((field) => ({
    fieldKey: field.key,
    fieldValue: valueOf(values, field.key),
  }));
}
